import FluxEventEmitter from '../FluxEventEmitter';
import Constants from '../Constants';
import assign from 'object-assign';
import Flux from '../Flux';
const CHANGE_EVENTS = {
	VideoData: 'videoDataChange',
	VideoDataLoad: 'videoDataLoad',
};

const _defaultFilter = {
	modelIds: [],
	actingGroups: [],
	ids: [],
	tags: [],
	releasedFrom: null,
	releasedUntil: null,
	types: [],
};

const _defaultFilterForCategory = {
	allVideos: {
		actingGroups: [],
		types: [
			{
				free: null,
				premium: true,
				discount: null,
				vip: null,
				bought: false,
				trending: null,
				vxselect: null,
				vip30: null,
				classic: false,
				shared: null,
				original: true,
			},
		],
	},
	newVideos: {
		actingGroups: [],
		types: [
			{
				free: null,
				premium: true,
				discount: null,
				vip: null,
				bought: false,
				trending: null,
				vxselect: null,
				vip30: null,
				classic: false,
				shared: null,
				original: true,
			},
		],
	},
	discountedVideos: {
		actingGroups: [],
		types: [
			{
				free: false,
				premium: true,
				discount: true,
				vip: null,
				bought: false,
				trending: null,
				vxselect: null,
				vip30: null,
				classic: false,
				shared: null,
				original: true,
			},
		],
	},
	trendingVideos: {
		actingGroups: [],
		releasedFrom: new Date(new Date().setDate(new Date().getDate() - 8)),
		types: [
			{
				free: false,
				premium: true,
				discount: null,
				vip: null,
				bought: false,
				trending: true,
				vxselect: null,
				vip30: null,
				classic: false,
				shared: null,
				original: true,
			},
		],
	},
	freeVideos: {
		actingGroups: [Constants.Vxql.Videos.ActingGroups.FEMALES],
		types: [
			{
				free: true,
				premium: null,
				discount: null,
				vip: null,
				bought: false,
				trending: null,
				vxselect: null,
				vip30: null,
				classic: null,
				shared: null,
				original: true,
			},
		],
	},
	topVideos: {
		actingGroups: [],
		releasedFrom: new Date(new Date().setMonth(new Date().getMonth() - 9)),
		types: [
			{
				free: null,
				premium: null,
				discount: null,
				vip: null,
				bought: false,
				trending: null,
				vxselect: null,
				vip30: null,
				classic: null,
				shared: null,
				original: true,
			},
		],
	},
	vip30Videos: {
		types: [
			{
				free: null,
				premium: null,
				discount: null,
				vip: null,
				bought: null,
				trending: null,
				vxselect: null,
				vip30: true,
				classic: null,
				shared: null,
				original: null,
			},
		],
	},
	boughtVideos: {
		types: [
			{
				free: null,
				premium: null,
				discount: null,
				vip: null,
				bought: true,
				trending: null,
				vxselect: null,
				vip30: null,
				classic: null,
				shared: null,
				original: true,
			},
		],
	},
};

const _defaultOrderForCategory = {
	allVideos: Constants.Vxql.Videos.SortOrders.TOP_RATED,
	discountedVideos: Constants.Vxql.Videos.SortOrders.NEWEST,
	trendingVideos: Constants.Vxql.Videos.SortOrders.TOP_TRENDING,
	freeVideos: Constants.Vxql.Videos.SortOrders.NEWEST,
	topVideos: Constants.Vxql.Videos.SortOrders.BEST_SELLING,
	vip30Videos: Constants.Vxql.Videos.SortOrders.TOP_RATED,
	boughtVideos: Constants.Vxql.Videos.SortOrders.NEWEST,
	newVideos: Constants.Vxql.Videos.SortOrders.NEWEST,
};

const _videoData = {};
const _videoDataLoading = {};

const VideoV2Store = assign({}, FluxEventEmitter.prototype, {
	EVENTS: CHANGE_EVENTS,

	/**
	 * @param {string} category
	 * @returns {Object}
	 */
	getDefaultFilterForCategory: function (category) {
		if (!_defaultFilterForCategory[category]) {
			throw new Error('Unmapped category');
		}

		const categoryFilter = _defaultFilterForCategory[category];

		return { ..._defaultFilter, ...categoryFilter };
	},

	/**
	 * @param {string} category
	 * @returns {Object}
	 */
	getDefaultOrderForCategory: function (category) {
		if (!_defaultOrderForCategory[category]) {
			throw new Error('Unmapped category');
		}
		return _defaultOrderForCategory[category];
	},

	/**
	 * @param {Object} filter
	 * @param {string} order
	 * @returns {Object[]|null}
	 */
	getVideos: function (filter, order) {
		let videos = null;
		const key = getKey(filter, order);
		if (_videoData[key]) {
			videos = _videoData[key]['items'] || null;
		}
		return videos;
	},

	/**
	 * @param {Object} filter
	 * @param {string} order
	 * @returns {*|null}
	 */
	getVideoTotalCount: function (filter, order) {
		let total = null;
		const key = getKey(filter, order);
		if (_videoData[key]) {
			total = _videoData[key]['total'] || 0;
		}
		return total;
	},

	/**
	 *
	 * @param {Object} filter
	 * @param {string} order
	 * @returns {boolean}
	 */
	isVideoDataLoading: function (filter, order) {
		const key = getKey(filter, order);

		return _videoDataLoading[key] || false;
	},

	clearCache: function () {
		// remove all keys from cache objects
		[_videoData].forEach((obj) => {
			for (const key in obj) {
				delete obj[key];
			}
		});
	},

	// Direct load function (replacing action dispatching)
	loadVideos: function (filter, order, count, offset = 0, id = null) {
		filter = getShapedFilter(filter);
		const key = getKey(filter, order);

		_videoDataLoading[key] = true;
		VideoV2Store.emit(CHANGE_EVENTS.VideoDataLoad);

		const getParams = new URLSearchParams(window.location.search);
		if (getParams.has('testSatisfaction')) {
			order = 'satisfactionScore';
		}

		const fetchVideos = filter.modelFilter
			? Flux.Vxql.getVideosWithModelFilterFromVXQL(filter.filter, filter.modelFilter, order, count, offset)
			: Flux.Vxql.getVideosFromVXQL(filter, order, count, offset);

		fetchVideos.then(({ data }) => {
			const newVideoData = data.videos; //@TODO: handle error

			if (newVideoData) {
				let videoData = _videoData[key];

				if (!videoData) {
					videoData = {};
				}

				videoData.total = newVideoData.total;

				let videos = videoData.items;

				if (!videos) {
					videos = {};
					videoData.items = videos;
				}

				for (const [index, video] of newVideoData.items.entries()) {
					videos[offset + index] = video;
				}

				_videoData[key] = videoData;

				_videoDataLoading[key] = false;

				VideoV2Store.emit(CHANGE_EVENTS.VideoDataLoad);
				VideoV2Store.emit(CHANGE_EVENTS.VideoData, id);
			}
		});
	},
});

function getKey(filter, order) {
	return JSON.stringify({ ...getShapedFilter(filter), order }); //@TODO: hash the stringified object?
}

function getShapedFilter(filter) {
	// add missing filter values and sets defined order of key(so objects can be compared using stringify)
	return { ..._defaultFilter, ...filter };
}

export default VideoV2Store;
